<template>
  <!-- Single element root: Teleport alone is treated as a fragment for fallthrough attrs / listeners. -->
  <div class="tb-base-modal-root">
    <Teleport to="body">
      <Transition name="modal-backdrop">
        <div
          v-if="modelValue"
          :class="['fixed inset-0 flex items-center justify-center p-4', zIndexClass]"
          @click.self="onBackdropClick"
        >
          <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

          <Transition name="modal-dialog" appear>
            <div
              v-if="modelValue"
              role="dialog"
              :aria-modal="true"
              :class="[
                'relative z-10 w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]',
                sizeClass,
              ]"
            >
              <div class="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex min-w-0 flex-1 items-start gap-3">
                  <div v-if="$slots.headerIcon" class="tb-modal-header-icon">
                    <slot name="headerIcon" />
                  </div>
                  <div class="min-w-0">
                    <h2 :class="titleHeadingClass">{{ title }}</h2>
                    <p v-if="subtitle" class="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
                      {{ subtitle }}
                    </p>
                  </div>
                </div>
                <button
                  v-if="closable"
                  type="button"
                  class="shrink-0 self-start p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                  :aria-label="'Close'"
                  @click="closeDialog"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div class="flex-1 overflow-y-auto px-6 py-5">
                <slot />
              </div>

              <div
                v-if="$slots.footer"
                class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-white/[0.02]"
              >
                <slot name="footer" />
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  size?: 'sm' | 'md' | 'lg'
  closable?: boolean
  closeOnBackdrop?: boolean
  zIndexClass?: string
}>(), {
  subtitle: undefined,
  size: 'md',
  closable: true,
  closeOnBackdrop: true,
  zIndexClass: 'z-[120]',
})

// Runtime event names so the compiler registers them (avoids extraneous @close warnings with Teleport root).
const emit = defineEmits(['update:modelValue', 'close'])

const sizeClass = computed(() => {
  if (props.size === 'sm') return 'max-w-sm'
  if (props.size === 'lg') return 'max-w-2xl'
  return 'max-w-lg'
})

const titleHeadingClass = computed(() => {
  const base = 'font-semibold leading-snug text-gray-900 dark:text-white'
  if (props.size === 'lg') return `text-lg sm:text-xl ${base}`
  return `text-base ${base}`
})

function closeDialog(): void {
  emit('update:modelValue', false)
  emit('close')
}

function onBackdropClick(): void {
  if (props.closeOnBackdrop) closeDialog()
}

function onEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.modelValue && props.closable) closeDialog()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) document.addEventListener('keydown', onEscape)
    else document.removeEventListener('keydown', onEscape)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscape)
})
</script>

<style scoped>
/* No layout box; keeps a real component root for Vue without affecting flex/grid parents. */
.tb-base-modal-root {
  display: contents;
}

.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-dialog-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.modal-dialog-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-dialog-enter-from,
.modal-dialog-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
.modal-dialog-enter-to,
.modal-dialog-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
